import {
  Outlet,
  RootRoute,
  Route,
  Router,
  createMemoryHistory,
  redirect,
  useNavigate,
} from '@tanstack/react-router'
import { useEffect } from 'react'
import { AppShell } from '../components/shell/AppShell'
import { RequireRole } from '../auth/require-role'
import { NotFoundPage } from '../pages/NotFoundPage'
import { ErrorPage } from '../pages/ErrorPage'
import { LoginPage } from '../pages/LoginPage'
import { ConnectIlmuchatPage } from '../pages/ConnectIlmuchatPage'
import { HomePage } from '../pages/HomePage'
import { MyLeavePage } from '../pages/MyLeavePage'
import { ApplyLeavePage } from '../pages/ApplyLeavePage'
import { WfhPage } from '../pages/WfhPage'
import { WfhRegisterPage } from '../pages/WfhRegisterPage'
import { CalendarPage } from '../pages/CalendarPage'
import { ManagerTeamPage } from '../pages/ManagerTeamPage'
import { ManagerDashboardPage } from '../pages/ManagerDashboardPage'
import { ManagerRequestsPage } from '../pages/ManagerRequestsPage'
import { HrEmployeesPage } from '../pages/HrEmployeesPage'
import { HrOrgChartPage } from '../pages/HrOrgChartPage'
import { HrBlackoutsPage } from '../pages/HrBlackoutsPage'
import { HrPeakPeriodsPage } from '../pages/HrPeakPeriodsPage'
import { HrLeavesHub } from '../pages/hr-leaves-hub/HrLeavesHub'
import { HrApproverCoverPage } from '../pages/HrApproverCoverPage'
import { HrHolidaysPage } from '../pages/HrHolidaysPage'
import { HrSettingsPage } from '../pages/HrSettingsPage'
import { AdminHubPage } from '../pages/admin/AdminHubPage'
import { BrandingThemePage } from '../pages/admin/BrandingThemePage'
import { AdminBackgroundJobsPage } from '../pages/admin/AdminBackgroundJobsPage'
import { HrPayrollPage } from '../pages/HrPayrollPage'
import { HrAuditPage } from '../pages/HrAuditPage'
import { LeaveDetailPage } from '../pages/LeaveDetailPage'
import { DirectorDashboardPage } from '../pages/DirectorDashboardPage'
import { DirectorCarryForwardPage } from '../pages/director/DirectorCarryForwardPage'

import { HodDigestPage } from '../pages/hod/HodDigestPage'
import { MePage } from '../pages/employee/MePage'
import { EmailActionConfirmationPage } from '../pages/email-action/EmailActionConfirmationPage'
import { MyAccountPage } from '../pages/MyAccountPage'
import { ChangePasswordRequiredPage } from '../pages/ChangePasswordRequiredPage'
import { HrEmployeeFlagsPage } from '../pages/HrEmployeeFlagsPage'
import {
  HrPatternsPage,
  ManagerPatternsPage,
} from '../pages/hr/HrPatternsPage'
import { HrSlackPage } from '../pages/HrSlackPage'
import { HrEmailPage } from '../pages/HrEmailPage'
import { HrCalendarPage } from '../pages/HrCalendarPage'
import { HrBulkImportPage } from '../pages/HrBulkImportPage'
import { HrLeaveBalanceImportPage } from '../pages/HrLeaveBalanceImportPage'
import { HrLeaveHistoryImportPage } from '../pages/HrLeaveHistoryImportPage'
import { HrLeaveHistoryExportPage } from '../pages/HrLeaveHistoryExportPage'
import { HrCoverageHub } from '../pages/hr-coverage/HrCoverageHub'
import { HrTeamsPage } from '../pages/HrTeamsPage'
import { HrOrgStructurePage } from '../pages/HrOrgStructurePage'
import { HrImportExportPage } from '../pages/HrImportExportPage'
import { AtomicDesignPage } from '../pages/AtomicDesignPage'
import { flags } from '../lib/feature-flags'

const rootRoute = new RootRoute({
  component: () => <Outlet />,
})

// Public route — login (no AppShell)
const loginRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

// Public route — ILMUchat account-link verify gate (no AppShell). The page
// itself enforces auth (bounces to /login?next= when no session).
const connectIlmuchatRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/connect/ilmuchat',
  component: ConnectIlmuchatPage,
})

// Authenticated layout group
const appLayoutRoute = new Route({
  getParentRoute: () => rootRoute,
  id: 'app-layout',
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
})

const homeRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: HomePage,
})

const myLeaveRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/my-leave',
  component: () => (
    <RequireRole minimum="employee">
      <MyLeavePage />
    </RequireRole>
  ),
})

const applyRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/apply',
  component: () => (
    <RequireRole minimum="employee">
      <ApplyLeavePage />
    </RequireRole>
  ),
})

// /wfh — work-from-home requests (employee apply/list + manager approvals).
// Flag-gated: when OFF the route falls through to NotFound so the surface is
// fully hidden, matching the nav item gate on `flags.wfhRequest`.
const wfhRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/wfh',
  // `?tab=approvals&focus=<requestId>` — the Slack card's "Open in LMS" link
  // deep-links to the approvals tab with the pending row highlighted.
  validateSearch: (search: Record<string, unknown>): Record<string, string> => {
    const out: Record<string, string> = {}
    if (search['tab'] === 'request' || search['tab'] === 'mine' || search['tab'] === 'approvals') {
      out['tab'] = search['tab'] as string
    }
    if (typeof search['focus'] === 'string' && search['focus'] !== '') {
      out['focus'] = search['focus'] as string
    }
    return out
  },
  component: () =>
    flags.wfhRequest ? (
      <RequireRole minimum="employee">
        <WfhPage />
      </RequireRole>
    ) : (
      <NotFoundPage />
    ),
})

// /wfh/register — HR attendance register (every WFH / out-on-business record,
// all teams). Flag-gated + HR+ only; the api enforces the role too.
const wfhRegisterRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/wfh/register',
  component: () =>
    flags.wfhRegister ? (
      <RequireRole minimum="hr">
        <WfhRegisterPage />
      </RequireRole>
    ) : (
      <NotFoundPage />
    ),
})

const calendarRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/calendar',
  component: () => (
    <RequireRole minimum="employee">
      <CalendarPage />
    </RequireRole>
  ),
})

// /manager/team — People Roster (spec 2026-06-11). Flag OFF restores the
// historical CalendarPage mount (the prototype-port behaviour) as the
// kill-switch posture.
const managerTeamRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/manager/team',
  // Persist the open person's id in the URL so opening a leave detail from the
  // person modal and closing it (history.back) restores the same person modal
  // instead of snapping back to the bare roster. Mirrors /manager/requests'
  // tab round-trip — without a declared schema TanStack strips search on
  // navigate and the modal (component-local state) is lost on remount.
  validateSearch: (search: Record<string, unknown>): Record<string, string> => {
    const out: Record<string, string> = {}
    if (typeof search['person'] === 'string' && search['person'] !== '') {
      out['person'] = search['person'] as string
    }
    return out
  },
  component: () => (
    <RequireRole minimum="manager">
      {flags.managerTeamRoster ? <ManagerTeamPage /> : <CalendarPage />}
    </RequireRole>
  ),
})

const managerRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/manager',
  component: () => (
    <RequireRole minimum="manager">
      <ManagerDashboardPage />
    </RequireRole>
  ),
})

const managerRequestsRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/manager/requests',
  // Declare the search schema so the active `tab` + history `filter` round-trip
  // through the URL. Without it, TanStack strips search on client navigate and
  // the tab was component-local state only — so opening a leave detail and
  // closing it (history.back) remounted the page and snapped back to Pending.
  // Persisting the tab in the URL makes closing the modal restore the same tab.
  // `blackoutId` + `leave` are the existing deep-link params (Slack / signals).
  validateSearch: (search: Record<string, unknown>): Record<string, string> => {
    const out: Record<string, string> = {}
    if (search['tab'] === 'escalated' || search['tab'] === 'history') {
      out['tab'] = search['tab'] as string
    }
    if (
      search['filter'] === 'approved' ||
      search['filter'] === 'rejected'
    ) {
      out['filter'] = search['filter'] as string
    }
    for (const k of ['blackoutId', 'leave']) {
      if (typeof search[k] === 'string' && search[k] !== '') out[k] = search[k] as string
    }
    return out
  },
  component: () => (
    <RequireRole minimum="manager">
      <ManagerRequestsPage />
    </RequireRole>
  ),
})

// HR landing — the HR dashboard is the role's home (`/`, HrHomeDashboard),
// same as every other role. /hr has no nav entry of its own; redirect any
// old bookmark / deep link to Home so there's a single HR overview surface.
const hrRedirectRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr',
  component: function HrRedirect() {
    const navigate = useNavigate()
    useEffect(() => {
      void navigate({ to: '/', replace: true })
    }, [navigate])
    return null
  },
})

const hrEmployeesRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/employees',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrEmployeesPage />
    </RequireRole>
  ),
})

const hrOrgChartRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/org-chart',
  component: () => (
    <RequireRole minimum="hod" only={['hod', 'hr', 'director', 'exec_director']}>
      <HrOrgChartPage />
    </RequireRole>
  ),
})

const hrPatternsRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/patterns',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrPatternsPage />
    </RequireRole>
  ),
})

const hrSlackRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/slack',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrSlackPage />
    </RequireRole>
  ),
})

const hrEmailRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/email',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrEmailPage />
    </RequireRole>
  ),
})

const hrCalendarRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/calendar',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrCalendarPage />
    </RequireRole>
  ),
})

const hrBulkImportRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/import',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrBulkImportPage />
    </RequireRole>
  ),
})

const hrLeaveImportRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/leave-import',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrLeaveBalanceImportPage />
    </RequireRole>
  ),
})

const hrLeaveHistoryImportRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/leave-history-import',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrLeaveHistoryImportPage />
    </RequireRole>
  ),
})

const hrLeaveHistoryExportRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/leave-history-export',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrLeaveHistoryExportPage />
    </RequireRole>
  ),
})

// Manager Patterns — same operational board as HR, but the api
// RBAC-scopes the data to the manager's direct reports.
const managerPatternsRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/manager/patterns',
  component: () => (
    <RequireRole minimum="manager">
      <ManagerPatternsPage />
    </RequireRole>
  ),
})

const hrEmployeeFlagsRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/employees/$employeeId/flags',
  component: function HrEmployeeFlagsRouteComponent() {
    const { employeeId } = hrEmployeeFlagsRoute.useParams()
    return (
      <RequireRole minimum="hr" only={['hr', 'exec_director']}>
        <HrEmployeeFlagsPage employeeId={employeeId} />
      </RequireRole>
    )
  },
})

const hrBlackoutsRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/blackouts',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrBlackoutsPage />
    </RequireRole>
  ),
})

const hrPeakPeriodsRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/peak-periods',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrPeakPeriodsPage />
    </RequireRole>
  ),
})

const hrLeavesRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/leaves',
  // Without validateSearch, TanStack's client-side navigate strips the `search`
  // object (see PersonModal note), so the FilterBar's navigate({ search }) was a
  // no-op and filters appeared dead. Declaring the schema lets the params
  // round-trip through the URL. `tab` drives the Leaves hub's active tab.
  validateSearch: (search: Record<string, unknown>): Record<string, string> => {
    const out: Record<string, string> = {}
    // `q` is the shared free-text employee-name filter, persisted across the
    // hub's tabs (Applied Leaves / Entitlement / Balances) so switching tabs
    // or opening a modal keeps the search. See lib/use-url-search.
    for (const k of ['tab', 'q', 'type', 'status', 'manager', 'team_id', 'since', 'until', 'flag', 'year']) {
      if (typeof search[k] === 'string' && search[k] !== '') out[k] = search[k] as string
    }
    return out
  },
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrLeavesHub />
    </RequireRole>
  ),
})

const hrApproverCoverRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/approver-cover',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      {flags.approverOverride ? (
        <HrApproverCoverPage />
      ) : (
        <p style={{ padding: 24 }}>This feature is not enabled.</p>
      )}
    </RequireRole>
  ),
})

// Back-compat redirects — the retired EA-delegation paths now land on the
// Approver cover page (EA delegation was retired 2026-08-05).
const hrDelegationsRedirectRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/delegations',
  component: function HrDelegationsRedirect() {
    const navigate = useNavigate()
    useEffect(() => {
      void navigate({ to: '/hr/approver-cover', replace: true })
    }, [navigate])
    return null
  },
})

const hrEARedirectRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/ea',
  component: function HrEARedirect() {
    const navigate = useNavigate()
    useEffect(() => {
      void navigate({ to: '/hr/approver-cover', replace: true })
    }, [navigate])
    return null
  },
})

const hrHolidaysRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/holidays',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrHolidaysPage />
    </RequireRole>
  ),
})

// Replaced by the Leaves hub (Replacement Grants tab). The old path redirects
// so existing bookmarks/deep-links still resolve.
const hrReplacementGrantsRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/replacement-grants',
  beforeLoad: () => {
    throw redirect({ to: '/hr/leaves', search: { tab: 'replacement' } })
  },
})

const hrCoverageThresholdsRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/coverage',
  // Preserve the ?tab= param for the Thresholds / Change requests hub.
  validateSearch: (search: Record<string, unknown>): Record<string, string> => {
    const out: Record<string, string> = {}
    if (
      search['tab'] === 'thresholds' ||
      search['tab'] === 'requests' ||
      search['tab'] === 'history'
    ) {
      out['tab'] = search['tab'] as string
    }
    return out
  },
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrCoverageHub />
    </RequireRole>
  ),
})

const hrTeamsRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/teams',
  component: () =>
    flags.hrTeamsManagement ? (
      <RequireRole minimum="hr" only={['hr', 'exec_director']}>
        <HrTeamsPage />
      </RequireRole>
    ) : (
      <NotFoundPage />
    ),
})

const hrOrgStructureRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/org-structure',
  component: () =>
    flags.orgStructureAdmin ? (
      <RequireRole minimum="hr" only={['hr', 'exec_director']}>
        <HrOrgStructurePage />
      </RequireRole>
    ) : (
      <NotFoundPage />
    ),
})

const hrSettingsRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/settings',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrSettingsPage />
    </RequireRole>
  ),
})

const adminHubRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/admin',
  component: () =>
    flags.adminHub ? (
      <RequireRole minimum="admin">
        <AdminHubPage />
      </RequireRole>
    ) : (
      <NotFoundPage />
    ),
})

const adminBrandingRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/branding',
  component: () =>
    flags.adminHub ? (
      <RequireRole minimum="superadmin">
        <BrandingThemePage />
      </RequireRole>
    ) : (
      <NotFoundPage />
    ),
})

const adminJobsRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/admin/jobs',
  component: () =>
    flags.adminBackgroundJobs ? (
      <RequireRole minimum="admin">
        <AdminBackgroundJobsPage />
      </RequireRole>
    ) : (
      <NotFoundPage />
    ),
})

const hrPayrollRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/payroll',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrPayrollPage />
    </RequireRole>
  ),
})

const hrAuditRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/audit',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrAuditPage />
    </RequireRole>
  ),
})

const hrActingApprovalsRedirectRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/acting-approvals',
  component: function HrActingApprovalsRedirect() {
    const navigate = useNavigate()
    useEffect(() => {
      void navigate({ to: '/hr/approver-cover', replace: true })
    }, [navigate])
    return null
  },
})

// Cancel-approved is now a row action on the Leaves hub's Applications tab.
// The old standalone path redirects there.
const hrCancelLeaveRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/cancel-leave',
  beforeLoad: () => {
    throw redirect({ to: '/hr/leaves', search: { tab: 'applications' } })
  },
})

const leaveDetailRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/leave',
  // LeaveDetailPage reads `id` from search. Declaring the schema lets
  // client-side navigate() preserve it — TanStack strips undeclared search on
  // client navigation, which is why some callers had to fall back to a native
  // <a href> (full-page reload). With the schema, callers navigate in-app and
  // the detail opens without a flash.
  validateSearch: (search: Record<string, unknown>): { id?: string } => {
    const out: { id?: string } = {}
    if (typeof search['id'] === 'string' && search['id'] !== '') out['id'] = search['id'] as string
    return out
  },
  component: () => (
    <RequireRole minimum="employee">
      <LeaveDetailPage />
    </RequireRole>
  ),
})

// The Department → Overview page was retired; its at-a-glance stats folded
// into the Team page (headcount, on-leave-this-week, next holiday) and its
// escalations moved to the unified Approvals page. The path redirects so
// existing links (Slack HOD digests, home-lobby cards, bookmarks) still land.
const hodOverviewRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hod/overview',
  beforeLoad: () => {
    throw redirect({ to: '/manager/team' })
  },
})

// The standalone Escalations queue folded into the unified Approvals page —
// escalated-to-me leaves now live in /manager/requests' "For approvals" tab.
// Redirect old bookmarks, Slack DMs, and dashboard links to the merged surface.
const directorEscalationsRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/director/escalations',
  component: function DirectorEscalationsRedirect() {
    const navigate = useNavigate()
    useEffect(() => {
      void navigate({ to: '/manager/requests', replace: true })
    }, [navigate])
    return null
  },
})

const directorHomeRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/director',
  component: () => (
    <RequireRole minimum="director">
      <DirectorDashboardPage />
    </RequireRole>
  ),
})

// Back-compat redirect — overflow/unpaid director decisions now live in the
// unified Approvals page's "For approvals" tab (was /director/escalations).
// Old bookmarks, notifications, and dashboard links keep working.
const directorLeavesRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/director/leaves',
  component: function DirectorLeavesRedirect() {
    const navigate = useNavigate()
    useEffect(() => {
      void navigate({ to: '/manager/requests', replace: true })
    }, [navigate])
    return null
  },
})

const directorCarryForwardRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/director/carry-forward',
  component: () => (
    <RequireRole minimum="director">
      <DirectorCarryForwardPage />
    </RequireRole>
  ),
})

// Replaced by the Leaves hub (Entitlement tab). Redirect the old path so
// bookmarks resolve.
const hrCreditsRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/credits',
  beforeLoad: () => {
    throw redirect({ to: '/hr/leaves', search: { tab: 'entitlement' } })
  },
})

// Tabbed hubs consolidating the former flat Leave-ops items. `?tab=` must be
// declared or TanStack's client-side navigate strips it, dropping the tab.
const hrImportExportRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/import-export',
  validateSearch: (search: Record<string, unknown>): { tab?: string } =>
    typeof search['tab'] === 'string' ? { tab: search['tab'] } : {},
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <HrImportExportPage />
    </RequireRole>
  ),
})

// The Adjustments hub folded into the Leaves hub. Redirect its old path to the
// Entitlement tab (per-employee credit admin).
const hrAdjustmentsRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/adjustments',
  beforeLoad: () => {
    throw redirect({ to: '/hr/leaves', search: { tab: 'entitlement' } })
  },
})


const hodDigestRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/hod/digest',
  component: () => (
    <RequireRole minimum="hod">
      <HodDigestPage />
    </RequireRole>
  ),
})

const meProfileRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/me',
  component: () => (
    <RequireRole minimum="employee">
      <MePage />
    </RequireRole>
  ),
})

// Public route — no auth required (lands from email link)
const emailActionConfirmRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/email-action/confirm',
  component: EmailActionConfirmationPage,
})

// Auth-required but renders OUTSIDE the AppShell — calm single-card
// interstitial for users flagged `mustChangePassword`. The AppShell
// itself routes to this path when the flag is true; rendering outside
// the shell keeps the user in a single-purpose UI with no nav.
const changePasswordRequiredRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/change-password-required',
  component: ChangePasswordRequiredPage,
})

const meAccountRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/me/account',
  component: () => (
    <RequireRole minimum="employee">
      <MyAccountPage />
    </RequireRole>
  ),
})

const atomicDesignRoute = new Route({
  getParentRoute: () => appLayoutRoute,
  path: '/atomic-design',
  component: () => (
    <RequireRole minimum="hr" only={['hr', 'exec_director']}>
      <AtomicDesignPage />
    </RequireRole>
  ),
})


const routeTree = rootRoute.addChildren([
  loginRoute,
  connectIlmuchatRoute,
  emailActionConfirmRoute,
  changePasswordRequiredRoute,
  appLayoutRoute.addChildren([
    homeRoute,
    myLeaveRoute,
    applyRoute,
    wfhRoute,
    wfhRegisterRoute,
    calendarRoute,
    managerTeamRoute,
    managerRoute,
    managerRequestsRoute,
    managerPatternsRoute,
    hrRedirectRoute,
    hrEmployeesRoute,
    hrBlackoutsRoute,
    hrPeakPeriodsRoute,
    hrLeavesRoute,
    hrApproverCoverRoute,
    hrEARedirectRoute,
    hrDelegationsRedirectRoute,
    hrHolidaysRoute,
    hrReplacementGrantsRoute,
    hrCoverageThresholdsRoute,
    hrTeamsRoute,
    hrOrgStructureRoute,
    hrCreditsRoute,
    hrImportExportRoute,
    hrAdjustmentsRoute,
    hrSettingsRoute,
    adminHubRoute,
    adminBrandingRoute,
    adminJobsRoute,
    hrPayrollRoute,
    hrAuditRoute,
    hrOrgChartRoute,
    hrPatternsRoute,
    hrSlackRoute,
    hrEmailRoute,
    hrCalendarRoute,
    hrBulkImportRoute,
    hrLeaveImportRoute,
    hrLeaveHistoryImportRoute,
    hrLeaveHistoryExportRoute,
    hrEmployeeFlagsRoute,
    hrActingApprovalsRedirectRoute,
    hrCancelLeaveRoute,
    leaveDetailRoute,
    hodOverviewRoute,
    directorEscalationsRoute,
    directorHomeRoute,
    directorLeavesRoute,
    directorCarryForwardRoute,
    hodDigestRoute,
    meProfileRoute,
    meAccountRoute,
    atomicDesignRoute,
  ]),
])

export function createAppRouter(): Router<typeof routeTree> {
  return new Router({
    routeTree,
    defaultNotFoundComponent: NotFoundPage,
    defaultErrorComponent: ErrorPage,
  })
}

export function createTestRouter(initialPath = '/'): Router<typeof routeTree> {
  return new Router({
    routeTree,
    defaultNotFoundComponent: NotFoundPage,
    defaultErrorComponent: ErrorPage,
    history: createMemoryHistory({ initialEntries: [initialPath] }),
  })
}

export type AppRouter = ReturnType<typeof createAppRouter>

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter
  }
}
